import React from 'react';
import { Typography, Breadcrumbs, Link, Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

/**
 * PageBreadcrumbs Component
 * 
 * A flexible breadcrumb component that can handle various breadcrumb configurations.
 * 
 * @param {Array} items - Array of breadcrumb items
 * Each item can have:
 * - text (required): The display text
 * - icon (optional): React component/icon to display
 * - link (optional): If present, makes the item clickable
 * 
 * @param {Object} sx - Optional styling overrides
 * @param {number|string} mt - Margin top (default: 0)
 * @param {number|string} mb - Margin bottom (default: 1)
 * @param {number|string} ml - Margin left (default: 0)
 * Examples:
 * 
 * Simple title only:
 * <PageBreadcrumbs items={[{ text: "Pipelines", icon: <DataObjectIcon /> }]} />
 * 
 * With navigation:
 * <PageBreadcrumbs items={[
 *   { text: "Pipelines", icon: <DataObjectIcon />, link: "/pipelines" },
 *   { text: "Create a new Pipeline" }
 * ]} />
 * 
 * Multi-level navigation:
 * <PageBreadcrumbs items={[
 *   { text: "Pipelines", icon: <DataObjectIcon />, link: "/pipelines" },
 *   { text: "48e4b5bd-9ee9-4490-b819-db730e228e31", link: "/pipelines/48e4b5bd-9ee9-4490-b819-db730e228e31" },
 *   { text: "Edit Pipeline" }
 * ]} />
 */
const PageBreadcrumbs = ({ items = [], sx = {}, mt = 1.5, mb = 3, ml = 1 }) => {
  // If only one item, render as a simple title
  if (items.length === 1) {
    const item = items[0];
    return (
      <Box sx={{ mt, mb, ml }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            ...sx 
          }}
        >
          {item.icon && React.cloneElement(item.icon, { sx: { mr: 1 } })}
          {item.link ? (
            <Link 
              component={RouterLink} 
              to={item.link} 
              color="inherit" 
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              {item.text}
            </Link>
          ) : (
            item.text
          )}
        </Typography>
      </Box>
    );
  }

  // Multiple items - render as breadcrumbs
  return (
    <Box sx={{ mt, mb, ml }}>
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ ...sx }}
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          
          if (index === 0) {
            // First item gets h4 styling
            return (
              <Typography 
                key={index}
                variant="h4" 
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                {item.link ? (
                  <Link 
                    component={RouterLink} 
                    to={item.link} 
                    color="inherit" 
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    {item.icon && React.cloneElement(item.icon, { sx: { mr: 1 } })}
                    {item.text}
                  </Link>
                ) : (
                  <>
                    {item.icon && React.cloneElement(item.icon, { sx: { mr: 1 } })}
                    {item.text}
                  </>
                )}
              </Typography>
            );
          }
          
          if (isLast) {
            // Last item is not clickable and uses primary text color
            return (
              <Typography 
                key={index}
                color="text.primary" 
                sx={{ 
                  fontWeight: 'medium',
                  mt: -0.2, 
                  display: 'flex', 
                  alignItems: 'center' 
                }}
              >
                {item.icon && React.cloneElement(item.icon, { sx: { mr: 1 } })}
                {item.text}
              </Typography>
            );
          }
          
          // Middle items
          return item.link ? (
            <Link 
              key={index}
              component={RouterLink} 
              to={item.link} 
              color="inherit" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontWeight: 'medium'
              }}
            >
              {item.icon && React.cloneElement(item.icon, { sx: { mr: 1 } })}
              {item.text}
            </Link>
          ) : (
            <Typography 
              key={index}
              color="text.secondary"
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontWeight: 'medium'
              }}
            >
              {item.icon && React.cloneElement(item.icon, { sx: { mr: 1 } })}
              {item.text}
            </Typography>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default PageBreadcrumbs; 